import React, { useState } from 'react';
import { X, CreditCard, DollarSign, Receipt } from 'lucide-react';
import { api } from '../../auth/authStore';

interface PaymentModalProps {
  isOpen: boolean;
  invoiceId: string | null;
  invoiceNumber: string;
  amountDue: number;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: DollarSign },
  { value: 'check', label: 'Check', icon: Receipt },
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
  { value: 'debit_card', label: 'Debit Card', icon: CreditCard },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: Receipt },
  { value: 'e_transfer', label: 'E-Transfer', icon: Receipt },
  { value: 'other', label: 'Other', icon: Receipt },
];

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  invoiceId,
  invoiceNumber,
  amountDue,
  currency,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    method: 'cash',
    amount: amountDue,
    transactionId: '',
    notes: '',
    paidAt: new Date().toISOString().split('T')[0],
  });

  const formatCurrency = (amount: number, curr: string = 'CAD') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: curr,
    }).format(amount);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.method) {
      newErrors.method = 'Payment method is required';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Payment amount must be greater than 0';
    }

    if (formData.amount > amountDue) {
      newErrors.amount = 'Payment amount cannot exceed amount due';
    }

    if (!formData.paidAt) {
      newErrors.paidAt = 'Payment date is required';
    }

    if (formData.method === 'check' && !formData.transactionId.trim()) {
      newErrors.transactionId = 'Check number is required for check payments';
    }

    if ((formData.method === 'credit_card' || formData.method === 'debit_card') && !formData.transactionId.trim()) {
      newErrors.transactionId = 'Transaction ID is required for card payments';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !invoiceId) {
      return;
    }

    setLoading(true);
    try {
      const paymentData = {
        method: formData.method,
        amount: formData.amount,
        transactionId: formData.transactionId || undefined,
        notes: formData.notes || undefined,
        paidAt: new Date(formData.paidAt).toISOString(),
      };

      await api.post(`/firm-admin/billing/invoices/${invoiceId}/payments`, paymentData);
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Failed to add payment:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Failed to add payment' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      method: 'cash',
      amount: amountDue,
      transactionId: '',
      notes: '',
      paidAt: new Date().toISOString().split('T')[0],
    });
    setErrors({});
    onClose();
  };

  const handleAmountChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData({ ...formData, amount: numValue });
  };

  const setFullAmount = () => {
    setFormData({ ...formData, amount: amountDue });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Record Payment</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Invoice Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Invoice:</span>
              <span className="text-sm text-gray-900">{invoiceNumber}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Amount Due:</span>
              <span className="text-sm font-semibold text-red-600">
                {formatCurrency(amountDue, currency)}
              </span>
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method *
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_METHODS.map((method) => {
                const IconComponent = method.icon;
                return (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, method: method.value })}
                    className={`flex items-center justify-center p-3 border rounded-md text-sm font-medium transition-colors ${
                      formData.method === method.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-2" />
                    {method.label}
                  </button>
                );
              })}
            </div>
            {errors.method && (
              <p className="mt-1 text-sm text-red-600">{errors.method}</p>
            )}
          </div>

          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Amount *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={amountDue}
                value={formData.amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                className={`w-full pl-7 pr-12 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.amount ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0.00"
              />
              <button
                type="button"
                onClick={setFullAmount}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-blue-600 hover:text-blue-800"
              >
                Full
              </button>
            </div>
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
            )}
          </div>

          {/* Transaction ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {formData.method === 'check' ? 'Check Number' : 
               formData.method === 'credit_card' || formData.method === 'debit_card' ? 'Transaction ID' :
               formData.method === 'bank_transfer' || formData.method === 'e_transfer' ? 'Reference Number' :
               'Transaction/Reference ID'}
              {(formData.method === 'check' || formData.method === 'credit_card' || formData.method === 'debit_card') && ' *'}
            </label>
            <input
              type="text"
              value={formData.transactionId}
              onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.transactionId ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={
                formData.method === 'check' ? 'Enter check number' :
                formData.method === 'credit_card' || formData.method === 'debit_card' ? 'Enter transaction ID' :
                'Enter reference number (optional)'
              }
            />
            {errors.transactionId && (
              <p className="mt-1 text-sm text-red-600">{errors.transactionId}</p>
            )}
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Date *
            </label>
            <input
              type="date"
              value={formData.paidAt}
              onChange={(e) => setFormData({ ...formData, paidAt: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.paidAt ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.paidAt && (
              <p className="mt-1 text-sm text-red-600">{errors.paidAt}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Additional notes about this payment (optional)"
            />
          </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Recording...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Record Payment
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
